"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import type { CampusLocation } from "@repo/actions/AcademyApi/GetCampuses";
import { CampusLocationSelector as SharedCampusLocationSelector } from "@repo/campus-location-selector";
import { useAtom } from "jotai";
import { campusResolutionSettledAtom, learningFormatAtom, selectedCampusAtom, selectedModalitySlugAtom } from "@/app/atoms/courseSelections";
import type { CoursePageSection } from "@repo/sanity-utils/query-types";
import { setSelectedCampusCookie } from "@repo/campus-location-selector/server";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import { applyCampusConfirmation } from "./campusConfirmation";
export type CampusLocationSelectorProps = {
  campuses: CampusLocation[];
  modalityName: string;
  coursePageSection?: NonNullable<CoursePageSection>;
  /** When set, confirming a campus also selects this modality (WEL-1696). */
  modalityId?: string | null;
  modalitySlug?: string | null;
  courseSlug?: string | null;
  /**
   * Campus slugs from this modality's course offerings. Empty = available at
   * every campus. Non-empty = auto-select only when the confirmed campus is listed.
   */
  offeringCampusIds?: readonly string[];
};

/**
 * aops course-page adapter: jotai atoms + selected-campus cookie.
 * UI lives in `@repo/campus-location-selector` (single-select).
 */
export function CampusLocationSelector(t0) {
  const $ = _c(22);
  const {
    campuses,
    modalityName,
    coursePageSection,
    modalityId: t1,
    modalitySlug: t2,
    courseSlug: t3,
    offeringCampusIds: t4
  } = t0;
  const modalityId = t1 === undefined ? null : t1;
  const modalitySlug = t2 === undefined ? null : t2;
  const courseSlug = t3 === undefined ? null : t3;
  let t5;
  if ($[0] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const offeringCampusIds = t5;
  let t6;
  if ($[2] !== coursePageSection?.keyedCopy) {
    t6 = coursePageSection?.keyedCopy ?? [];
    $[2] = coursePageSection?.keyedCopy;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const keyedCopy = t6;
  const [selectedCampusId, setSelectedCampusId] = useAtom(selectedCampusAtom);
  const [, setCampusResolutionSettled] = useAtom(campusResolutionSettledAtom);
  const [, setSelectedLearningFormat] = useAtom(learningFormatAtom);
  const [, setSessionModalitySlug] = useAtom(selectedModalitySlugAtom);
  const hasRefreshedCookie = useRef(false);
  let t7;
  let t8;
  if ($[4] !== selectedCampusId) {
    t7 = () => {
      if (!selectedCampusId || hasRefreshedCookie.current) {
        return;
      }
      hasRefreshedCookie.current = true;
      setSelectedCampusCookie(selectedCampusId);
    };
    t8 = [selectedCampusId];
    $[4] = selectedCampusId;
    $[5] = t7;
    $[6] = t8;
  } else {
    t7 = $[5];
    t8 = $[6];
  }
  useEffect(t7, t8);
  let t9;
  if ($[7] !== courseSlug || $[8] !== modalityId || $[9] !== modalitySlug || $[10] !== offeringCampusIds || $[11] !== setCampusResolutionSettled || $[12] !== setSelectedCampusId || $[13] !== setSelectedLearningFormat || $[14] !== setSessionModalitySlug) {
    t9 = async selection => {
      const campusId = typeof selection === "string" ? selection : selection[0];
      if (!campusId) {
        return;
      }
      await setSelectedCampusCookie(campusId);
      applyCampusConfirmation({
        campusId,
        modalityId,
        modalitySlug,
        courseSlug,
        offeringCampusIds
      }, {
        setSelectedCampusId,
        setCampusResolutionSettled,
        setSelectedLearningFormat,
        setSessionModalitySlug,
        trackModalitySelected: _temp
      });
    };
    $[7] = courseSlug;
    $[8] = modalityId;
    $[9] = modalitySlug;
    $[10] = offeringCampusIds;
    $[11] = setCampusResolutionSettled;
    $[12] = setSelectedCampusId;
    $[13] = setSelectedLearningFormat;
    $[14] = setSessionModalitySlug;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== campuses || $[17] !== keyedCopy || $[18] !== modalityName || $[19] !== selectedCampusId || $[20] !== t9) {
    t10 = <SharedCampusLocationSelector campuses={campuses} modalityName={modalityName} keyedCopy={keyedCopy} multiple={false} value={selectedCampusId} onConfirm={t9} />;
    $[16] = campuses;
    $[17] = keyedCopy;
    $[18] = modalityName;
    $[19] = selectedCampusId;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}
function _temp(properties) {
  sendDiscoveryEventBeacon("course_modality_selected", properties);
}